import { FormCodeInput as UiFormCodeInput } from '@Pages/pageRuntime/components/ui/form/FormCodeInput';
import type React from 'react';

type FormCodeInputProps = {
  /**
   * Field label.
   */
  label?: React.ReactNode;

  /**
   * Help text shown below the input.
   */
  helpText?: React.ReactNode;

  /**
   * Validation errors shown below the input.
   */
  error?: string | string[];

  /**
   * Mark the field as required.
   */
  isRequired?: boolean | null;

  /**
   * Actions shown beside the label.
   */
  labelActions?: React.ReactNode;

  /**
   * The digits typed so far, in order.
   */
  value?: string;

  /**
   * Called with the digits typed so far on every change.
   */
  onChange?: (value: string) => void;

  /**
   * Number of digits in the code.
   */
  length?: number;

  /**
   * Called with the full code once every cell holds a digit. Verify the code here.
   */
  onComplete?: (code: string) => void;

  /**
   * Disable the input, for example while the code is being verified.
   */
  disabled?: boolean;

  /**
   * Focus the first cell when the input appears.
   */
  autoFocus?: boolean;

  className?: string;
};

/**
 * A one-time code (OTP) input with one cell per digit. Paste, backspace, and the phone
 * keyboard's autofill from an incoming text all work, and only digits are accepted.
 * `onComplete` fires when the last cell is filled. This is a form primitive like
 * `FormPhoneInput`: it sends and verifies nothing itself. Clear `value` and set `error`
 * when the code is wrong.
 *
 * @example
 * <Sdk.Components.FormCodeInput
 *   label="Enter the code we texted you"
 *   value={code}
 *   onChange={setCode}
 *   onComplete={(code) => verify.run({ code })}
 *   error={verify.errorMessage}
 * />
 */
export function FormCodeInput({
  label = null,
  length = 6,
  className,
  ...props
}: FormCodeInputProps) {
  return (
    <UiFormCodeInput label={label} length={length} containerClassName={className} {...props} />
  );
}
